import React, {ReactElement, useCallback, useState} from "react";
import {
    Chip,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TableCell,
    Tooltip,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SeatingPlanIcon from "@mui/icons-material/Deck";
import NeighbourhoodIcon from "@mui/icons-material/Cottage";
import DeskIcon from "@mui/icons-material/TableRestaurant";
import MembersIcon from "@mui/icons-material/People";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {NeighbourhoodSnapshot} from "../../api/Model";
import {DataTable} from "../../components/data-table/DataTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {dateTimeString} from "../../util/DateUtil";
import {Principal} from "../../components/principal/Principal";

type SnapshotActions = {
    onCreatePlan?: (snapshot: NeighbourhoodSnapshot) => void;
    onEdit?: (snapshot: NeighbourhoodSnapshot) => void;
    onDelete?: (snapshot: NeighbourhoodSnapshot) => void;
};

const SnapshotActionContext = React.createContext<SnapshotActions>({});

function ContentChip({title, icon, count}: {
    title: string;
    icon: ReactElement;
    count: number;
}) {
    return (
        <Tooltip title={title}>
            <Chip
                variant="outlined"
                icon={icon}
                slotProps={{
                    root: {sx: {pl: 1}}
                }}
                label={count}
            />
        </Tooltip>
    );
}

function ActionColumn({snapshot}: { snapshot: NeighbourhoodSnapshot }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const {onCreatePlan, onEdit, onDelete} = React.useContext(SnapshotActionContext);

    const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);
    const closeMenu = useCallback(() => setAnchorEl(null), []);
    const createPlan = useCallback(() => {
        setAnchorEl(null);
        onCreatePlan?.(snapshot);
    }, [onCreatePlan, snapshot]);
    const edit = useCallback(() => {
        setAnchorEl(null);
        onEdit?.(snapshot);
    }, [onEdit, snapshot]);
    const remove = useCallback(() => {
        setAnchorEl(null);
        onDelete?.(snapshot);
    }, [onDelete, snapshot]);

    const hasActions = onCreatePlan || onEdit || onDelete;

    return (
        <TableCell align="right">
            {hasActions && (
                <>
                    <Tooltip title="Actions">
                        <IconButton size="small" onClick={openMenu}>
                            <MoreVertIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                        {onCreatePlan && (
                            <MenuItem onClick={createPlan}>
                                <ListItemIcon><SeatingPlanIcon fontSize="small"/></ListItemIcon>
                                <ListItemText>Create seating plan</ListItemText>
                            </MenuItem>
                        )}
                        {onEdit && (
                            <MenuItem onClick={edit}>
                                <ListItemIcon><EditIcon fontSize="small"/></ListItemIcon>
                                <ListItemText>Edit snapshot</ListItemText>
                            </MenuItem>
                        )}
                        {onDelete && (
                            <MenuItem onClick={remove}>
                                <ListItemIcon><DeleteIcon fontSize="small" color="error"/></ListItemIcon>
                                <ListItemText>Delete snapshot</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}
        </TableCell>
    );
}

const columns: TableColumnData<NeighbourhoodSnapshot>[] = [
    {
        key: 'name',
        header: 'Name',
        cell: snapshot => (
            <TableCell>
                <Typography variant="h6" component="span">
                    {snapshot.name}{' '}
                </Typography>
                {snapshot.description && (
                    <Typography variant="body2" color="text.secondary" component="span">
                        {snapshot.description}
                    </Typography>
                )}
            </TableCell>
        ),
    },
    {
        key: 'contents',
        header: 'Contents',
        width: '16em',
        cell: snapshot => (
            <TableCell>
                <Stack direction="row" gap={1}>
                    <ContentChip
                        title="Neighbourhoods"
                        icon={<NeighbourhoodIcon/>}
                        count={snapshot.neighbourhoodCount}
                    />
                    <ContentChip
                        title="Desks"
                        icon={<DeskIcon/>}
                        count={snapshot.deskCount}
                    />
                    <ContentChip
                        title="Members"
                        icon={<MembersIcon/>}
                        count={snapshot.employeeCount}
                    />
                </Stack>
            </TableCell>
        ),
    },
    {
        key: 'created-at',
        header: 'Created',
        width: '20em',
        cell: snapshot => (
            <TableCell>
                <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="body2" component="span">
                        <Principal principalEmail={snapshot.createdBy}/>
                    </Typography>
                    <Typography variant="body2">
                        {dateTimeString(new Date(snapshot.createdAt))}
                    </Typography>
                </Stack>
            </TableCell>
        ),
    },
    {
        key: 'actions',
        header: '',
        width: '4em',
        align: 'right',
        cell: snapshot => <ActionColumn snapshot={snapshot}/>,
    },
];

export function NeighbourhoodSnapshotTable({rows, ...actions}: {
    rows: NeighbourhoodSnapshot[];
} & SnapshotActions) {
    return (
        <SnapshotActionContext.Provider value={actions}>
            <DataTable rows={rows} columns={columns} id={snapshot => snapshot.id}/>
        </SnapshotActionContext.Provider>
    );
}
